interface CacheEntry<T> {
    data: T
    timestamp: number
}

// In-memory cache map
const serverCache = new Map<string, CacheEntry<any>>()

/**
 * Runs the fetchFn and caches the result on the server-side.
 * Returns cached data if available and not expired.
 * For client-side execution, it bypasses the cache entirely.
 */
export async function withServerCache<T>(
    key: string,
    fetchFn: () => Promise<T>,
    ttlMs: number = 60 * 1000 // default 1 minute TTL
): Promise<T> {
    const isServer = typeof window === "undefined"

    if (isServer) {
        const cached = serverCache.get(key)
        if (cached && Date.now() - cached.timestamp < ttlMs) {
            return cached.data
        }
    }

    // Call the actual fetching function
    const data = await fetchFn()

    if (isServer) {
        // Run simple garbage collection if the cache grows too large
        if (serverCache.size > 1000) {
            const now = Date.now()
            for (const [k, entry] of serverCache.entries()) {
                if (now - entry.timestamp > 15 * 60 * 1000) { // clean anything older than 15 mins
                    serverCache.delete(k)
                }
            }
            // If still too large, clear everything to prevent any leaks
            if (serverCache.size > 1000) {
                serverCache.clear()
            }
        }

        serverCache.set(key, {
            data,
            timestamp: Date.now(),
        })
    }

    return data
}
